import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { WidgetInstance } from '../stores/dashboard-store';
import { useDeferredReady } from './use-deferred-ready';

export interface SharedDashboard {
  id: string;
  name: string;
  icon: string | null;
  widgets: WidgetInstance[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const QUERY_KEY = ['shared-dashboards'] as const;

export function useSharedDashboards() {
  // #1429: non-critical shell data — defer off the cold-load critical path.
  const deferredReady = useDeferredReady();
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<{ data: SharedDashboard[] }>('/shared-dashboards');
      return res.data.data;
    },
    enabled: deferredReady,
  });
}

export interface CreateSharedDashboardInput {
  name: string;
  icon?: string | null;
  widgets?: WidgetInstance[];
}

export function useCreateSharedDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSharedDashboardInput) => {
      const res = await api.post<{ data: SharedDashboard }>('/shared-dashboards', input);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['dashboard-permissions'] });
    },
  });
}

export interface UpdateSharedDashboardInput {
  id: string;
  name?: string;
  icon?: string | null;
  widgets?: WidgetInstance[];
}

export function useUpdateSharedDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: UpdateSharedDashboardInput) => {
      const res = await api.patch<{ data: SharedDashboard }>(`/shared-dashboards/${id}`, rest);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteSharedDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/shared-dashboards/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['dashboard-permissions'] });
    },
  });
}
